import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react'
import { collection, doc, getDoc, onSnapshot, setDoc } from 'firebase/firestore'
import { db } from '../firebase/firebase'
import { AuthContext } from './AuthContext'
import axiosPost from '../Global/Axios/axiosPost'
import handleError from '../Global/HandleError/handleError'
import axiosGet from '../Global/Axios/axiosGet'
import { BatchContext } from './BatchContext'
import { ClassContext } from './ClassContext'

export const CommandContext = createContext()

const CommandContextProvider = (props) => {
  const { authState } = useContext(AuthContext)
  const {
    setAVState,
    AVState,
    setIsChatEnabled,
    disableChatForClass,
    setDisableChatForClass,
    isChatEnabled,
    giveAVPermissionToStudent,
    setGiveAVPermissionToStudent,
    enableBlur,
    setEnableBlur,
  } = useContext(ClassContext)
  const { getAuthHeader } = useContext(AuthContext)
  const { batchByCode, isRecording, setIsRecording } = useContext(BatchContext)
  const [isNewMessage, setIsNewMessage] = useState(false)
  const [teacherDoubts, setTeacherDoubts] = useState([])
  const [studentDoubt, setStudentDoubt] = useState(false)
  const [teacherLeft] = useState(false)
  const [banStudent, setBanStudent] = useState(false)
  // Initial value of publish video should be false, otherwise all students joining will publish their feed. Problem for large class. Setting true because Android app not working.
  const [publishVideo] = useState(true)
  const [notificationState, setNotificationState] = useState({
    open: false,
    uid: null,
    type: null,
    content: null,
  })

  const [isDeviceControlsWithUser, setIsDeviceControlsWithUser] = useState(true)
  const [permissionRequest, setPermissionRequest] = useState({
    audio: false,
    video: false,
  })
  const [permissionResolution, setPermissionResolution] = useState({})
  // eslint-disable-next-line no-unused-vars
  const [permissionTimeouts, setPermissionTimeouts] = useState({})

  // quality selection for students -
  /*
   * 0 - auto
   * 1 - high
   * 2 - medium
   * 3 - low (can default to audio)
   */
  const [qualitySelection, setQualitySelection] = useState(0)

  let timeout

  const permissionCollectionRef = useRef()

  useEffect(() => {
    if (authState.role === 'T') return
    if (!batchByCode || !batchByCode?.lecture?.id) return
    return onSnapshot(
      doc(
        db,
        import.meta.env.VITE_FIREBASE_ENV,
        import.meta.env.VITE_FIRESTORE_DB_VERSION,
        'owners',
        `${batchByCode.owner}`,
        'batches',
        `${batchByCode.id.toLowerCase()}`,
        'lectures',
        `${batchByCode.lecture.id}`,
        'userControls',
        'controls',
      ),
      (snapshot) => {
        const data = snapshot.data()
        // Before teacher joins or if no controls are set, allow students to control mic/cam
        const micCamAllowed =
          typeof data?.micCamAllowed === 'boolean' ? data.micCamAllowed : true
        const chatAllowed =
          typeof data?.chatAllowed === 'boolean' ? data.chatAllowed : true

        setIsChatEnabled(chatAllowed)
        setIsDeviceControlsWithUser(micCamAllowed)
      },
    )
  }, [batchByCode?.lecture?.id, batchByCode])

  useEffect(() => {
    if (authState.role !== 'T') return
    if (!batchByCode || !batchByCode?.lecture?.id) return
    permissionCollectionRef.current = collection(
      db,
      import.meta.env.VITE_FIREBASE_ENV,
      import.meta.env.VITE_FIRESTORE_DB_VERSION,
      'owners',
      `${batchByCode.owner}`,
      'batches',
      `${batchByCode.id.toLowerCase()}`,
      'lectures',
      `${batchByCode.lecture.id}`,
      'userControls',
    )
  }, [batchByCode?.lecture?.id, batchByCode])

  useEffect(() => {
    const updatePermissions = async () => {
      if (authState.role !== 'T' || !permissionCollectionRef.current) return

      const docu = await getDoc(
        doc(permissionCollectionRef.current, 'controls'),
      )

      if (docu.exists()) {
        const data = docu.data()
        if (
          data.chatAllowed &&
          data.chatAllowed === !disableChatForClass &&
          data.micCamAllowed &&
          data.micCamAllowed === giveAVPermissionToStudent
        ) {
          return
        }
      }

      await setDoc(doc(permissionCollectionRef.current, 'controls'), {
        chatAllowed: !disableChatForClass,
        micCamAllowed: giveAVPermissionToStudent,
        updatedAt: new Date().getTime(),
      })
    }

    updatePermissions()
  }, [
    permissionCollectionRef.current,
    disableChatForClass,
    giveAVPermissionToStudent,
  ])

  useEffect(() => {
    if (authState.role === 'T') {
      setIsDeviceControlsWithUser(true)
    }
  }, [authState.role])

  const RaiseQuery = async (lecture_id) => {
    if (!lecture_id) return
    try {
      const res = await axiosPost(`/query/`, {
        data: {
          lecture_id,
        },
        headers: getAuthHeader(),
      })
      return res
    } catch (err) {
      return false
    }
  }

  const GetAllQueriesInALecture = async (lecture_id) => {
    if (!lecture_id) return
    try {
      const res = await axiosGet(`/query/?lecture=${lecture_id}`, {
        headers: getAuthHeader(),
      })
      setTeacherDoubts(() =>
        res.data.map((data) => ({
          id: data.id,
          senderId: data.student_id,
          state: data.status === 'D' ? 0 : 2,
        })),
      )
      return res
    } catch (err) {
      setTeacherDoubts([])
      handleError(err)
    }
  }

  const ResolveQuery = async (query_id) => {
    if (!query_id) return
    try {
      const res = await axiosPost(`/query/${query_id}/resolve/`, {
        data: {},
        headers: getAuthHeader(),
      })
      return res
    } catch (err) {
      return false
    }
  }

  function releaseNotification() {
    if (timeout) {
      clearTimeout(timeout)
    }
    timeout = setTimeout(() => {
      setNotificationState({
        open: false,
        uid: null,
        type: null,
        content: null,
      })
    }, 3500)
  }

  const setNotification = (uid, type, content) => {
    setNotificationState({
      open: true,
      uid,
      type,
      content,
    })
    releaseNotification()
  }

  const resolveAVRequest = (uid, deviceType, state) => {
    let device
    if (deviceType === 'mic') device = 'audio'
    else if (deviceType === 'cam') device = 'video'
    else return
    setPermissionResolution((permissions) => {
      const data = permissions?.[uid]
      if (data) {
        return {
          ...permissions,
          [uid]: {
            ...data,
            [device]: state,
          },
        }
      }
      return {
        ...permissions,
        [uid]: {
          [device]: state,
        },
      }
    })
    if (state === 'pending') {
      const t = setTimeout(() => {
        setPermissionResolution((permissions) => {
          const data = permissions?.[uid]
          if (data?.[device] === 'pending')
            return {
              ...permissions,
              [uid]: {
                ...data,
                [device]: 'unresponsive',
              },
            }
        })
      }, 10000)
      setPermissionTimeouts((timeouts) => {
        const timedata = timeouts?.[uid]
        if (timedata && timedata?.[device]) {
          clearTimeout(timedata?.[device])
        }
        return {
          ...timeouts,
          [uid]: { ...timedata, [device]: t },
        }
      })
    }
  }

  return (
    <CommandContext.Provider
      value={{
        AVState,
        setAVState,
        notificationState,
        setNotificationState,
        isDeviceControlsWithUser,
        setIsDeviceControlsWithUser,
        setNotification,
        teacherLeft,
        permissionRequest,
        setPermissionRequest,
        resolveAVRequest,
        permissionResolution,
        teacherDoubts,
        setTeacherDoubts,
        studentDoubt,
        setStudentDoubt,
        RaiseQuery,
        ResolveQuery,
        GetAllQueriesInALecture,
        publishVideo,
        isNewMessage,
        setIsNewMessage,
        disableChatForClass,
        setDisableChatForClass,
        isChatEnabled,
        setIsChatEnabled,
        isRecording,
        banStudent,
        setBanStudent,
        qualitySelection,
        setQualitySelection,
        setIsRecording,
        giveAVPermissionToStudent,
        setGiveAVPermissionToStudent,
        enableBlur,
        setEnableBlur,
      }}
    >
      {props.children}
    </CommandContext.Provider>
  )
}
export default CommandContextProvider
